import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchImageSearchListingPage } from '../../application/services/ImageSearchBatchDesignService';
import { IMAGE_SEARCH_PAGE_PATH } from '../../constants';
import {
  getImageSearchListingKeySuffix,
  readImageSearchPayloadFromSessionStorage,
} from '../../utils/imageSearchStorage';
import { getPlpBrowseTagsForApi, getPlpListingKey } from '@/features/plp/utils/plpUrl';
import type { ListingPageResult } from '@/features/plp/domain/models/ProductListing';

const EMPTY_LISTING: ListingPageResult = {
  header: '',
  totalCount: 0,
  filters: [],
  designs: [],
};

type ImageSearchLocationState = { fromImageSearch?: boolean; ts?: number } | null;

export function useImageSearchPlp() {
  const location = useLocation();
  const navigationTs = (location.state as ImageSearchLocationState)?.ts ?? null;

  const designIds = useMemo(() => {
    if (typeof window === 'undefined') return [];
    // Re-read sessionStorage when image-search navigation identity changes.
    const navigationIdentity = `${location.key}:${navigationTs ?? ''}`;
    void navigationIdentity;
    return readImageSearchPayloadFromSessionStorage();
  }, [location.key, navigationTs]);

  const listingKey = useMemo(() => {
    const base = getPlpListingKey(IMAGE_SEARCH_PAGE_PATH, location.search);
    return `${base}#${getImageSearchListingKeySuffix(designIds, navigationTs)}`;
  }, [location.search, designIds, navigationTs]);

  const tags = useMemo(() => getPlpBrowseTagsForApi(IMAGE_SEARCH_PAGE_PATH), []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [navigationTs, designIds]);

  const fetchPage = useCallback(
    async (pageNumber: number, opts?: { signal?: AbortSignal }): Promise<ListingPageResult> => {
      if (pageNumber > 1) return EMPTY_LISTING;
      return fetchImageSearchListingPage(designIds, opts);
    },
    [designIds],
  );

  return {
    pathname: IMAGE_SEARCH_PAGE_PATH,
    listingKey,
    fetchPage,
    tags,
  };
}
